import cx from 'clsx'
import {makeAutoObservable} from 'mobx'
import {calculateProperties} from '../features/calculation/propertyCalculator'
import {silentTransformCssString} from '../features/css-style/cssTransform'
import {cfEventHandlers, cfRequiredProperties} from '../features/define/utils/integratedComponentFeatures'
import type {Model} from '../features/define/utils/Model'
import type {ActionEventHandler} from '../features/event/ActionEventHandler'
import {getArgumentFunction, isFunctionArgumentValue} from '../features/event/consts/functionArgument'
import {DidMountEvent, WillUnmountEvent} from '../features/event/eventNames'
import type {ActionData, EventName} from '../features/event/types'
import {ActionEventArgs} from '../features/event/utils/ActionEventArgs'
import type {ComponentPropertiesContext} from '../features/properties-context/ComponentPropertiesContext'
import {getDefaultPropertiesContext} from '../features/properties-context/getDefaultPropertiesContext'
import type {CssPart} from '../features/style/types'
import type {ComputeChildren} from '../utils/ComputeChildren'
import type {ComponentData} from '../utils/contexts/ComponentDataContext'
import {isPromise} from '../utils/isPromise'
import {nameObservable} from '../utils/observableNaming'
import type {ComponentStyle} from './ComponentStore'
import {ComponentStore} from './ComponentStore'
import type {ComponentStoreLocalizer} from './ComponentStoreLocalizer'
import {flattenNestedCSS, generateClass, reactStylesToCss} from './css'
import type {IComponentState} from './IComponentState'
import type {Store} from './Store'

const getCustomEventHandlers = (model: Model) => {
  const customHandlers = model.features[cfEventHandlers]
  if (customHandlers) return customHandlers as Record<EventName, ActionEventHandler>
}

const getHtmlAttributes = (componentStore: ComponentStore) => componentStore.htmlAttributes
  ?.reduce((result: Record<string, string>, {name, value}) => {
    try {
      result[name] = JSON.parse(value)
    } catch {
      result[name] = value
    }
    return result
  }, {})

const bindFunctionsInArgs = (e: ActionEventArgs, args: Record<string, any>) => {
  const functionEntries: Array<[string, (...fnArgs: unknown[]) => unknown]> = []

  for (const [key, value] of Object.entries(args)) {
    if (isFunctionArgumentValue(value)) {
      const fn = getArgumentFunction(value.body || '')
      functionEntries.push([key, fn])
    }
  }

  const boundArgs = {...args}
  for (const [key, fn] of functionEntries) {
    boundArgs[key] = (evtOrParam: any, argsOrParam: any, ...userArgs: any) => {
      if (!(evtOrParam instanceof ActionEventArgs) && argsOrParam !== boundArgs) {
        return fn(e, boundArgs, ...userArgs)
      }

      return fn(evtOrParam, argsOrParam, ...userArgs)
    }
  }

  return boundArgs
}

function createActionHandlersChain(store: Store, actionDataList: ActionData[]) {
  const actions = actionDataList.map(data => ({
    func: store.findAction(data).func,
    args: {...data.args}
  }))

  return async (e: ActionEventArgs) => {
    try {
      for (const {func, args} of actions) {
        const withBoundFnArgs = bindFunctionsInArgs(e, args)
        const result = func(e, withBoundFnArgs)

        if (isPromise(result)) await result
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.info('Action handler throws an error', e)
    }
  }
}

const createDefaultActionHandler = (componentState: ComponentState, eventName: EventName) => {
  return async (...args: unknown[]) => {
    const {data, store} = componentState
    const actionDataList = data.store.events?.[eventName] ?? []
    const handlersChain = createActionHandlersChain(store, actionDataList)
    const renderedProps = componentState.get
    const actionEventArgs = new ActionEventArgs(eventName, data, store, args, renderedProps, componentState.context.cellInfo)
    await handlersChain(actionEventArgs)
  }
}

const computeEvents = (componentState: ComponentState) => {
  const events = {} as Record<EventName, (...args: unknown[]) => Promise<void>>
  const {data} = componentState

  const eventHandlers = getCustomEventHandlers(data.model) ?? componentState.context.eventHandlers ?? {}

  const eventKeyArray = [
    ...Object.keys(data.store.events ?? {}),
    ...Object.keys(eventHandlers)
  ]

  const set = new Set(eventKeyArray)
  set.delete(DidMountEvent)
  set.delete(WillUnmountEvent)

  set.forEach((name) => {
    events[name] = async (...args: unknown[]) => {
      const handler = eventHandlers[name]
      if (handler) {
        const actionEventArgs = new ActionEventArgs(name, data, componentState.store, args, componentState.get,
          componentState.context.cellInfo)
        const handlerResult = handler(actionEventArgs)
        if (isPromise(handlerResult)) await handlerResult
      }

      const defaultHandler = createDefaultActionHandler(componentState, name)
      await defaultHandler(...args)
    }
  })

  return events
}

type ComponentCssData = {
  className: string
  styleSheet?: CSSStyleSheet
  flatCss?: string
}

/**
 * Calculates all the properties of the form view component.
 */
export class ComponentState implements IComponentState {
  #styles: Record<CssPart, ComponentCssData> = {
    css: {
      className: generateClass(),
    },
    wrapperCss: {
      className: generateClass('wc-'),
    }
  }

  /**
   * The ref object associated with this component in the viewer. **Internal use only.**
   */
  #componentRef: any

  /**
   * The context for working with component properties.
   */
  readonly context: ComponentPropertiesContext

  /**
   * Creates an instance that calculates the properties of the form viewer component.
   * @param data the data needed to display the component.
   * @param store the form viewer settings.
   * @param localizer the function to localize the properties of a component, returns a Record with localized properties.
   * @param computeChildren the function that calculates all child properties of a component.
   * @param context the context for working with component properties.
   */
  constructor(
    readonly data: ComponentData,
    readonly store: Store,
    readonly localizer: ComponentStoreLocalizer,
    readonly computeChildren: ComputeChildren,
    context?: ComponentPropertiesContext,
  ) {
    this.context = context ?? getDefaultPropertiesContext(data)
    this.#componentRef = null
    makeAutoObservable(this, undefined, {name: nameObservable('ComponentState', {key: data.key})})
  }

  /**
   * @inheritDoc
   */
  get get(): Record<string, any> {
    const propsWithoutChildren = this.propsWithoutChildren
    return Object.assign({}, propsWithoutChildren, {className: this.className}, this.children(propsWithoutChildren))
  }

  /**
   * @inheritDoc
   */
  get ownProps() {
    return {...this.propsWithoutChildren, className: this.className}
  }

  /**
   * @inheritDoc
   */
  get propsWithoutChildren(): Record<string, any> {
    const {htmlAttributes, ...calculated} = this.calculatedProps

    return Object.assign(
      {key: this.data.store.key},
      this.data.model.defaultProps,
      calculated,
      this.localizedProps,
      this.value,
      this.readOnly,
      this.required,
      this.disabled,
      this.events,
      htmlAttributes ?? this.htmlAttributes,
      this.style,
      this.data.userDefinedProps,
    )
  }

  /**
   * @returns the component's field value data, if the component can have a field value.
   */
  get value() {
    const valueProperty = this.context.valueProperty
    if (valueProperty) {
      return {[valueProperty.propertyName]: valueProperty.propertyValue}
    }
  }

  /**
   * @returns the read-only property of a component if the component has a read-only flag.
   */
  get readOnly() {
    if (this.data.model.readOnly) {
      return {[this.data.model.readOnly]: this.isReadOnly}
    }
  }

  /**
   * @returns the required property of a component if the component has a required flag.
   */
  get required() {
    const requiredProps = this.data.model.features[cfRequiredProperties] as string[] | undefined

    if (!requiredProps || !this.hasRequiredValidation) return

    const result: Record<string, boolean> = {}
    requiredProps.forEach(prop => result[prop] = true)
    return result
  }

  /**
   * @inheritDoc
   */
  get isReadOnly() {
    const selfReadOnly = this.data.model.readOnly && this.selfProps[this.data.model.readOnly]
    return this.store.formViewerPropsStore.readOnly
      || this.data.parent?.componentState.isReadOnly
      || (selfReadOnly ?? false)
  }

  /**
   * @returns the disabled property of a component if the component has a disabled flag.
   */
  get disabled() {
    if (this.data.model.disabled) {
      return {[this.data.model.disabled]: this.isDisabled}
    }
  }

  /**
   * @inheritDoc
   */
  get isDisabled() {
    const selfDisabled = this.data.model.disabled && this.selfProps[this.data.model.disabled]
    return this.store.formViewerPropsStore.disabled
      || this.data.parent?.componentState.isDisabled
      || (selfDisabled ?? false)
  }

  /**
   * @returns the values for all properties of the component, calculates the values of the calculated properties.
   */
  get calculatedProps() {
    return calculateProperties(this.data.store, this.data.dataRoot)
  }

  /**
   * @returns component localized properties.
   */
  get localizedProps() {
    return this.localizer(this.data.store)
  }

  /**
   * @returns the component event handlers that send events to the event bus.
   */
  get events() {
    return computeEvents(this)
  }

  /**
   * Calculates and returns className property.
   * @returns the Record that contains the className property for the component.
   */
  get className() {
    return cx(
      this.requiredClassName,
      this.propsWithoutChildren.className,
      this.#styles.css.className
    )
  }

  /**
   * @inheritDoc
   */
  get wrapperClassName() {
    return this.#styles.wrapperCss.className
  }

  /**
   * @returns the Record that contains the style property for the component.
   */
  get style() {
    return this.getStyleFromStylePart(this.data.store.style)
  }

  /**
   * @inheritDoc
   */
  get wrapperStyle() {
    return this.getStyleFromStylePart(this.data.store.wrapperStyle)
  }

  /**
   * @returns all arbitrary HTML attributes of the component.
   */
  get htmlAttributes() {
    return getHtmlAttributes(this.data.store)
  }

  /**
   * Calculates and returns all child components.
   * @param props the React component properties.
   * @returns the Record that contains the child components of a component.
   */
  children(props: any) {
    return this.computeChildren(this.data, props)
  }

  /**
   * @inheritDoc
   */
  onDidMount() {
    this.executeLifecycleEvent(DidMountEvent)
    this.applyAllStyles()
  }

  /**
   * @inheritDoc
   */
  onWillUnmount() {
    this.executeLifecycleEvent(WillUnmountEvent)
    this.cleanStyles()
  }

  /**
   * @inheritDoc
   */
  applyStyles(cssPart: CssPart, flatCss: string) {
    if (flatCss) {
      this.updateAdoptedStyleSheets(cssPart, flatCss)
    }
  }

  private applyAllStyles() {
    this.applyStyles('css', this.flatCss)
    this.applyStyles('wrapperCss', this.flatWrapperCss)
  }

  /**
   * Clears component styles.
   */
  private cleanStyles() {
    const styleSheets: CSSStyleSheet[] = []
    Object.values(this.#styles)
      .forEach(item => {
        if (item.styleSheet) styleSheets.push(item.styleSheet)
        item.styleSheet = undefined
        item.flatCss = undefined
      })

    if (styleSheets.length) {
      document.adoptedStyleSheets = document.adoptedStyleSheets
        .filter(existing => !styleSheets.includes(existing))
    }
  }

  /**
   * @inheritDoc
   */
  setRef = (object: any) => {
    this.#componentRef = object
  }

  /**
   * @inheritDoc
   */
  getRefValue = () => {
    return this.#componentRef
  }

  private executeLifecycleEvent(eventName: EventName) {
    const handler = createDefaultActionHandler(this, eventName)
    const mountEvent = new ActionEventArgs(eventName, this.data, this.store, [], this.get, this.context.cellInfo)
    handler(mountEvent).catch(console.error)
  }

  private getStyleFromStylePart(stylePart?: ComponentStyle) {
    const {viewMode} = this.store

    const anyCss = silentTransformCssString(stylePart?.any?.string)
    const viewModeCss = silentTransformCssString(stylePart?.[viewMode]?.string)

    if (anyCss || viewModeCss) return {style: {...anyCss, ...viewModeCss}}
  }

  /**
   * @inheritDoc
   */
  get flatCss() {
    return this.computeFlatCssForPart('css')
  }

  /**
   * @inheritDoc
   */
  get flatWrapperCss() {
    return this.computeFlatCssForPart('wrapperCss')
  }

  private computeFlatCssForPart(cssPart: CssPart): string {
    const {model, store} = this.data
    const {viewMode} = this.store

    const cssObjectAny = reactStylesToCss(Object.assign({},
      model[cssPart]?.any?.object,
      store[cssPart]?.any?.object
    ))

    const cssObjectDevice = reactStylesToCss(Object.assign({},
      model[cssPart]?.[viewMode]?.object,
      store[cssPart]?.[viewMode]?.object
    ))

    const anyStyles = store[cssPart]?.any?.string
      ? store[cssPart]?.any?.string?.replaceAll('\n', ' ')
      : ''

    const deviceStyles = store[cssPart]?.[viewMode]?.string
      ? store[cssPart]?.[viewMode]?.string?.replaceAll('\n', ' ')
      : ''

    const cssData = [cssObjectAny, cssObjectDevice, anyStyles, deviceStyles]
      .map(item => item?.trim())
      .filter(Boolean)

    if (!cssData.length) {
      return ''
    }

    const className = this.#styles[cssPart].className
    const css = `.${className}.${className} {`
      + cssData
        .join('\n')
      + '}'

    return flattenNestedCSS(css)
  }

  private updateAdoptedStyleSheets(cssPart: CssPart, flatCss: string) {
    try {
      const cssData = this.#styles[cssPart]
      const existing = cssData.styleSheet
      if (existing) {
        if (cssData.flatCss !== flatCss) {
          existing.replaceSync(flatCss)
          cssData.flatCss = flatCss
        }
        return
      }

      const sheet = new CSSStyleSheet()
      sheet.replaceSync(flatCss)
      cssData.styleSheet = sheet
      cssData.flatCss = flatCss
      document.adoptedStyleSheets.push(sheet)
    } catch (e) {
      console.error(e)
    }
  }

  private get selfProps() {
    const {htmlAttributes, ...calculated} = this.calculatedProps
    return {
      ...this.data.model.defaultProps,
      ...calculated,
      ...this.localizedProps,
      ...this.value,
      ...this.style,
      ...this.data.userDefinedProps,
    } as Record<string, any>
  }

  private get hasRequiredValidation() {
    return ComponentStore.hasValidationRule(this.data.store, 'required')
  }

  private get requiredClassName() {
    if (this.hasRequiredValidation) {
      return {required: true}
    }
  }
}
